import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  queryClient,
  apiRequest,
  ADMIN_LIST_PAGE_SIZE,
  resetAdminServicesQueries,
  fetchAdminServicesListJson,
  fetchAllAdminServicesMerged,
} from '@/lib/queryClient';
import {
  clonePagedListResponseData,
  computeAdminListRange,
  normalizeAdminServicesListPayload,
} from '@/lib/adminApi';
import { useAdminServicesCatalog, useAdminStaffCatalog } from '@/hooks/useAdminPagedCatalogs';
import { useToast } from '@/hooks/useToast';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import {
  AccessTime,
  Add,
  Close,
  ContentCut,
  Delete,
  Edit,
  ExpandMore,
  Label,
} from '@mui/icons-material';
import { formatCents, formatDuration } from '@/lib/utils';
import { validatePositiveInt, validateNonNegativeInt } from '@/lib/formValidation';
import {
  dialogActionsSx,
  dialogContentOutlinedFormSx,
  dialogPaperPropsLabelSafe,
} from '@/lib/dialogStyles';
import type { Service } from '@shared/schema';

interface ServiceFormData {
  name: string;
  description: string;
  category: string;
  durationMins: number;
  bufferBeforeMins: number;
  bufferAfterMins: number;
  priceCents: number;
  depositEnabled: boolean;
  depositType: string;
  depositValue: number;
  active: boolean;
  staffIds: string[];
  /** Epos product id for POST `CreateServiceBody` (directory rows from GET with `eposNowProductId`). */
  selectedEposNowProductId: string;
}

const defaultFormData: ServiceFormData = {
  name: '',
  description: '',
  category: 'General',
  durationMins: 60,
  bufferBeforeMins: 0,
  bufferAfterMins: 0,
  priceCents: 0,
  depositEnabled: false,
  depositType: 'fixed',
  depositValue: 0,
  active: true,
  staffIds: [],
  selectedEposNowProductId: '',
};

interface ServiceFieldErrors {
  durationMins?: string;
  bufferBeforeMins?: string;
  bufferAfterMins?: string;
  eposNowProductId?: string;
}

/** Booking rows with id `0` are placeholders until completed via POST (same as staff). Note: `"0"` is truthy — never branch on `if (editingId)` alone. */
function isPlaceholderServiceId(id: string | number | null | undefined): boolean {
  if (id == null) return false;
  const s = String(id).trim();
  if (s === "") return false;
  return s === "0" || Number(s) === 0;
}

function staffIdsToApi(staffIds: string[]) {
  const numericStaffIds = staffIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
  return numericStaffIds.length > 0 ? numericStaffIds : undefined;
}

/** Swagger-style POST body: `eposNowProductId` + scheduling / staff / deposit fields. */
function buildCreateServiceBody(formData: ServiceFormData): Record<string, unknown> {
  return {
    eposNowProductId: Number(formData.selectedEposNowProductId),
    durationMins: formData.durationMins,
    bufferBeforeMins: formData.bufferBeforeMins,
    bufferAfterMins: formData.bufferAfterMins,
    active: formData.active,
    depositEnabled: formData.depositEnabled,
    depositType: formData.depositType,
    depositValue: formData.depositValue,
    staffIds: staffIdsToApi(formData.staffIds),
  };
}

/** PATCH: only fields still editable in the dialog (no name / description / category / price). */
function serviceFormToPatchBody(data: ServiceFormData): Record<string, unknown> {
  return {
    durationMins: data.durationMins,
    bufferBeforeMins: data.bufferBeforeMins,
    bufferAfterMins: data.bufferAfterMins,
    active: data.active,
    depositEnabled: data.depositEnabled,
    depositType: data.depositType,
    depositValue: data.depositValue,
    staffIds: staffIdsToApi(data.staffIds),
  };
}

export default function AdminServices() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Latest `dialogOpen` for `Transition` onExited — avoid clearing edit state if user reopened before exit finished. */
  const dialogOpenRef = useRef(dialogOpen);
  dialogOpenRef.current = dialogOpen;
  const [formData, setFormData] = useState<ServiceFormData>(defaultFormData);
  const [fieldErrors, setFieldErrors] = useState<ServiceFieldErrors>({});
  const [renameFrom, setRenameFrom] = useState<string | null>(null);
  const [renameInput, setRenameInput] = useState('');
  const [deleteCategory, setDeleteCategory] = useState<string | null>(null);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [addCategoryInput, setAddCategoryInput] = useState('');
  const [servicesListPage, setServicesListPage] = useState(1);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const [servicesActiveFilter, setServicesActiveFilter] = useState<'all' | 'active'>('all');

  /** Create / placeholder flows need the Epos directory; category manager needs all rows for chips — load full catalog only then. */
  const isNewOfferingDialog = editingId === null || isPlaceholderServiceId(editingId);

  const servicesListQuery = useQuery({
    queryKey: ['/api/admin/services', 'page', servicesListPage, servicesActiveFilter] as const,
    /** Page/filter come from `queryKey` only — never from closure — so the fetch matches the cache key. */
    queryFn: async ({ queryKey }) => {
      const page = queryKey[2] as number;
      const filter = queryKey[3] as 'all' | 'active';
      const raw = await fetchAdminServicesListJson(page, ADMIN_LIST_PAGE_SIZE, {
        activeOnly: filter === 'active',
      });
      /** Avoid shared `data[]` reference across pages / cache (prevents rows stacking when paginating). */
      return clonePagedListResponseData(raw);
    },
    placeholderData: undefined,
    structuralSharing: false,
  });

  const servicesListNormalized = useMemo(
    () =>
      servicesListQuery.data != null
        ? normalizeAdminServicesListPayload(servicesListQuery.data)
        : null,
    [servicesListQuery.data],
  );

  

  const catalogMetadata = servicesListNormalized?.metadata ?? null;
  /** Rows for the current page: exactly what this query’s API response normalizes to. */
  const servicesList = servicesListNormalized?.services ?? [];
  const servicesListMeta = catalogMetadata;

  /** For Pagination + clamping current page when totals shrink; API may omit `totalPages`. */
  const servicesUiTotalPages = useMemo(() => {
    const m = catalogMetadata;
    if (!m) return 1;
    if (m.totalPages != null && m.totalPages > 0) return m.totalPages;
    const total = m.totalRecords;
    if (total == null || total <= 0) return 1;
    return Math.max(1, Math.ceil(total / (m.limit ?? ADMIN_LIST_PAGE_SIZE)));
  }, [catalogMetadata]);

  /**
   * True while we have no response yet for the current page/filter (e.g. first paint after changing page).
   * Once `data` exists, we render that response’s `services` as returned by the API.
   */
  const servicesListAwaitingData =
    servicesListQuery.isFetching && servicesListQuery.data == null;

  const {
    services: servicesFullCatalog,
    isLoading: loadingFullServicesCatalog,
  } = useAdminServicesCatalog({
    enabled: categoriesExpanded || (dialogOpen && isNewOfferingDialog),
  });

  /**
   * Keep the current page in range when totals change (e.g. delete last item on last page).
   * Do **not** clamp while `catalogMetadata` is missing: on pagination TanStack Query clears `data`
   * for the new key until the request finishes, `servicesUiTotalPages` falls back to `1`, and
   * `5 > 1` would reset the user to page 1 — the classic “must click twice” bug.
   */
  useEffect(() => {
    if (catalogMetadata == null) return;
    if (servicesListPage > servicesUiTotalPages) setServicesListPage(servicesUiTotalPages);
  }, [catalogMetadata, servicesUiTotalPages, servicesListPage]);
  const { staff: staffList, isLoading: loadingStaff } = useAdminStaffCatalog();
  const listOrStaffBootstrapping = loadingStaff || servicesListAwaitingData;

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiRequest('POST', '/api/admin/services', body),
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
      toast({ title: t('toasts.serviceCreated') });
      closeDialog();
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: ServiceFormData) =>
      apiRequest('PATCH', `/api/admin/services/${editingId}`, serviceFormToPatchBody(data)),
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
      toast({ title: t('toasts.serviceUpdated') });
      closeDialog();
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/services/${id}`),
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
      toast({ title: t('toasts.serviceDeleted') });
    },
  });

  const patchServiceMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => {
      if (isPlaceholderServiceId(id)) throw new Error('cannot patch placeholder offering');
      return apiRequest('PATCH', `/api/admin/services/${id}`, { active });
    },
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const renameCategoryMutation = useMutation({
    mutationFn: async ({ from, to }: { from: string; to: string }) => {
      const trimmed = to.trim();
      if (!trimmed) throw new Error(t('common.error'));
      const all = await fetchAllAdminServicesMerged();
      const list = all.filter((s) => (s.category || 'General') === from);
      await Promise.all(
        list.map((s) => apiRequest('PATCH', `/api/admin/services/${s.id}`, { category: trimmed }))
      );
    },
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
      toast({ title: t('toasts.categoryRenamed') });
      setRenameFrom(null);
      setRenameInput('');
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (cat: string) => {
      const all = await fetchAllAdminServicesMerged();
      const list = all.filter(
        (s) => (s.category || 'General') === cat && !isPlaceholderServiceId(s.id)
      );
      await Promise.all(
        list.map((s) => apiRequest('PATCH', `/api/admin/services/${s.id}`, { category: 'General' }))
      );
    },
    onSuccess: async () => {
      await resetAdminServicesQueries(queryClient);
      toast({ title: t('toasts.categoryDeleted') });
      setDeleteCategory(null);
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  function openCreate(overrides?: Partial<ServiceFormData>) {
    setEditingId(null);
    setFormData({ ...defaultFormData, ...overrides });
    setFieldErrors({});
    setDialogOpen(true);
  }

  function openEdit(service: Service) {
    setEditingId(service.id);
    const assignedStaff = (service.staffIds ?? []).map(String);
    const rawPid = service.eposNowProductId;
    const selectedEpos = rawPid != null && !Number.isNaN(Number(rawPid)) ? String(rawPid) : '';
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category || 'General',
      durationMins: service.durationMins,
      bufferBeforeMins: service.bufferBeforeMins || 0,
      bufferAfterMins: service.bufferAfterMins || 0,
      priceCents: service.priceCents,
      depositEnabled: service.depositEnabled || false,
      depositType: service.depositType || 'fixed',
      depositValue: service.depositValue || 0,
      active: service.active !== false,
      staffIds: assignedStaff,
      selectedEposNowProductId: selectedEpos,
    });
    setFieldErrors({});
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
  }

  /** Reset form identity after close animation so the title does not flash to "New" while the panel is still visible. */
  function handleServiceDialogExited() {
    if (!dialogOpenRef.current) {
      setEditingId(null);
      setFieldErrors({});
    }
  }

  function validateService(): ServiceFormData | null {
    const next: ServiceFieldErrors = {};
    if (!editingId || isPlaceholderServiceId(editingId)) {
      if (
        !formData.selectedEposNowProductId ||
        Number.isNaN(Number(formData.selectedEposNowProductId))
      ) {
        next.eposNowProductId = t('adminServices.eposProductSelectRequired');
      }
    }

    const durErr = validatePositiveInt(formData.durationMins, t('adminServices.durationLabel'), 1);
    if (durErr) next.durationMins = durErr;
    const bbErr = validateNonNegativeInt(
      formData.bufferBeforeMins,
      t('adminServices.bufferBefore')
    );
    if (bbErr) next.bufferBeforeMins = bbErr;
    const baErr = validateNonNegativeInt(formData.bufferAfterMins, t('adminServices.bufferAfter'));
    if (baErr) next.bufferAfterMins = baErr;

    setFieldErrors(next);
    if (Object.keys(next).length > 0) return null;
    return formData;
  }

  function handleSubmit() {
    const payload = validateService();
    if (!payload) return;
    if (editingId != null && !isPlaceholderServiceId(editingId)) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(buildCreateServiceBody(payload));
    }
  }

  function toggleStaff(staffId: string | number) {
    const id = String(staffId);
    setFormData((prev) => ({
      ...prev,
      staffIds: prev.staffIds.includes(id)
        ? prev.staffIds.filter((x) => x !== id)
        : [...prev.staffIds, id],
    }));
  }

  const outlinedLabelSx = {
    bgcolor: 'background.paper' as const,
    px: 0.75,
    borderRadius: 0.5,
  };

  /** GET `/services` rows that include `eposNowProductId` (Epos directory for POST). */
  const eposProductOptions = useMemo(
    () =>
      (servicesFullCatalog ?? []).filter(
        (s) => s.eposNowProductId != null && !Number.isNaN(Number(s.eposNowProductId))
      ),
    [servicesFullCatalog]
  );

  /** MUI Select only displays a value that matches a MenuItem; include current row if API id is missing from catalog options. */
  const eposSelectShowsSelected = useMemo(() => {
    const v = formData.selectedEposNowProductId;
    if (!v) return true;
    return eposProductOptions.some((row) => String(row.eposNowProductId) === v);
  }, [formData.selectedEposNowProductId, eposProductOptions]);

  const serviceDialogPaperProps = {
    ...dialogPaperPropsLabelSafe,
    sx: {
      overflow: 'visible' as const,
      borderRadius: 3,
    },
  };

  const groupedAll = useMemo(() => {
    if (!servicesFullCatalog?.length) return {};
    return servicesFullCatalog.reduce<Record<string, Service[]>>((acc, s) => {
      const cat = s.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s);
      return acc;
    }, {});
  }, [servicesFullCatalog]);

  const groupedPaged = useMemo(() => servicesList.reduce<Record<string, Service[]>>((acc, s) => {
      const cat = s.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s);
      return acc;
    }, {}), [servicesList]);

  /** Category sections follow API row order (Object insertion order = first-seen per page). */
  const categoryEntriesAll = useMemo(() => Object.entries(groupedAll), [groupedAll]);
  const categoryEntriesPaged = useMemo(() => Object.entries(groupedPaged), [groupedPaged]);
  const categoryCount = categoryEntriesAll.length;

  /** `computeAdminListRange` only uses `page`, `limit`, `totalRecords` (see adminApi). */
  const servicesListRange = useMemo(
    () =>
      computeAdminListRange(
        catalogMetadata
          ? {
              ...catalogMetadata,
              page: catalogMetadata.page ?? servicesListPage,
              limit: catalogMetadata.limit ?? ADMIN_LIST_PAGE_SIZE,
            }
          : null,
        servicesList.length,
      ),
    [catalogMetadata, servicesListPage, servicesList.length],
  );

  function openRenameCategory(cat: string) {
    setRenameFrom(cat);
    setRenameInput(cat);
  }

  function confirmRenameCategory() {
    if (!renameFrom) return;
    renameCategoryMutation.mutate({ from: renameFrom, to: renameInput });
  }

  function confirmAddCategory() {
    const name = addCategoryInput.trim();
    if (!name) return;
    setAddCategoryOpen(false);
    setAddCategoryInput('');
    openCreate();
  }

  return (
    <Box sx={{ p: 3, maxWidth: { xs: '100%', lg: 1400 }, mx: 'auto', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold" data-testid="text-services-title">
            {t('adminServices.title')}
          </Typography>
          <Typography color="text.secondary">{t('adminServices.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label={t('adminServices.showLabel')}
            value={servicesActiveFilter}
            onChange={(e) => {
              const v = e.target.value as 'all' | 'active';
              setServicesActiveFilter(v);
              setServicesListPage(1);
            }}
            sx={{ minWidth: 180 }}
            data-testid="select-services-active-filter"
          >
            <MenuItem value="all">{t('adminServices.filterAll')}</MenuItem>
            <MenuItem value="active">{t('adminServices.filterActiveOnly')}</MenuItem>
          </TextField>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => openCreate()}
            data-testid="button-add-service"
          >
            {t('adminServices.addService')}
          </Button>
        </Box>
      </Box>

      {!loadingStaff && (
        <Accordion
          expanded={categoriesExpanded}
          onChange={(_, expanded) => setCategoriesExpanded(expanded)}
          variant="outlined"
          disableGutters
          sx={{ mb: 3, '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="categories-panel"
            id="categories-header"
          >
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Label fontSize="small" color="action" />
              <Typography fontWeight={600}>{t('adminServices.manageCategories')}</Typography>
              <Chip
                size="small"
                label={
                  categoriesExpanded
                    ? loadingFullServicesCatalog
                      ? '…'
                      : categoryCount
                    : '—'
                }
                variant="outlined"
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('adminServices.categoriesHelper')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {categoryEntriesAll.map(([cat]) => (
                <Box
                  key={cat}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    pl: 1.25,
                    pr: 0.5,
                    py: 0.25,
                    borderRadius: 999,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
                    {cat}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t('adminServices.renameCategory')}
                    onClick={() => openRenameCategory(cat)}
                    data-testid={`button-rename-category-${cat.replace(/\s+/g, '-')}`}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t('common.delete')}
                    disabled={cat === 'General'}
                    onClick={() => setDeleteCategory(cat)}
                    data-testid={`button-delete-category-${cat.replace(/\s+/g, '-')}`}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => {
                  setAddCategoryInput('');
                  setAddCategoryOpen(true);
                }}
                sx={{
                  borderRadius: 999,
                  borderStyle: 'dashed',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
                data-testid="button-add-category"
              >
                {t('adminServices.addCategory')}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {listOrStaffBootstrapping ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}
          data-testid="services-list-loading"
        >
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      ) : (servicesListMeta?.totalRecords ?? 0) === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ContentCut sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('adminServices.emptyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('adminServices.emptyBody')}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => openCreate()}>
              {t('adminServices.addService')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {categoryEntriesPaged.map(([category, categoryServices]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  display: 'block',
                }}
              >
                {category}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {categoryServices.map((service, rowIndex) => {
                  const staffAssignedCount = service.staffIds?.length ?? 0;
                  /** API rows may repeat `id` (e.g. `0`); duplicate React keys break reconciliation and multiply cards. */
                  const rowKey = `${category}-p${servicesListPage}-r${rowIndex}-${String(service.id)}`;
                  return (
                    <Card key={rowKey} sx={{ opacity: service.active ? 1 : 0.65 }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                fontWeight={600}
                                data-testid={`text-service-name-${service.id}`}
                              >
                                {service.name}
                              </Typography>
                              {!service.active && (
                                <Chip size="small" label={t('adminServices.inactive')} />
                              )}
                              {service.depositEnabled && (
                                <Chip
                                  size="small"
                                  label={t('adminServices.depositRequiredChip')}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {service.description && (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {service.description}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap',
                                mt: 1,
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <AccessTime sx={{ fontSize: 16 }} />
                                {formatDuration(service.durationMins)}
                              </Typography>
                              <Typography variant="body2" fontWeight={500} color="text.primary">
                                {formatCents(service.priceCents)}
                              </Typography>
                              {staffAssignedCount > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  {t('adminServices.staffAssigned', { count: staffAssignedCount })}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}
                          >
                            {!isPlaceholderServiceId(service.id) && (
                              <Switch
                                size="small"
                                checked={service.active !== false}
                                onChange={(_, v) =>
                                  patchServiceMutation.mutate({ id: service.id, active: v })
                                }
                                disabled={patchServiceMutation.isPending}
                                inputProps={{ 'aria-label': t('adminServices.active') }}
                                data-testid={`switch-service-active-${service.id}`}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() => openEdit(service)}
                              data-testid={`button-edit-service-${service.id}`}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            {!isPlaceholderServiceId(service.id) && (
                              <IconButton
                                size="small"
                                onClick={() => deleteMutation.mutate(service.id)}
                                disabled={deleteMutation.isPending}
                                data-testid={`button-delete-service-${service.id}`}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {!servicesListAwaitingData &&
            servicesList.length > 0 &&
            servicesListMeta?.totalRecords != null &&
            servicesListMeta.totalRecords > 0 && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
                data-testid="box-services-pagination"
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  {servicesListRange
                    ? t('adminCommon.paginationRange', {
                        start: servicesListRange.start,
                        end: servicesListRange.end,
                        total: servicesListRange.total,
                      })
                    : null}
                </Typography>
                {servicesUiTotalPages > 1 && (
                  <Pagination
                    color="primary"
                    size="small"
                    count={servicesUiTotalPages}
                    page={servicesListPage}
                    onChange={(_, p) => setServicesListPage(p)}
                  />
                )}
              </Box>
            )}
        </>
      )}

      <Dialog
        open={Boolean(renameFrom)}
        onClose={() => setRenameFrom(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperPropsLabelSafe}
      >
        <DialogTitle>{t('adminServices.renameCategory')}</DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          <TextField
            autoFocus
            label={t('adminServices.newCategoryName')}
            value={renameInput}
            onChange={(e) => setRenameInput(e.target.value)}
            fullWidth
            data-testid="input-rename-category"
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setRenameFrom(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={confirmRenameCategory}
            disabled={renameCategoryMutation.isPending || !renameInput.trim()}
            data-testid="button-confirm-rename-category"
          >
            {renameCategoryMutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteCategory)}
        onClose={() => setDeleteCategory(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('adminServices.deleteCategoryTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteCategory
              ? t('adminServices.deleteCategoryBody', { category: deleteCategory })
              : null}
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setDeleteCategory(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteCategory && deleteCategoryMutation.mutate(deleteCategory)}
            disabled={deleteCategoryMutation.isPending || !deleteCategory}
            data-testid="button-confirm-delete-category"
          >
            {deleteCategoryMutation.isPending ? t('common.saving') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addCategoryOpen}
        onClose={() => setAddCategoryOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperPropsLabelSafe}
      >
        <DialogTitle>{t('adminServices.addCategory')}</DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          <TextField
            autoFocus
            label={t('adminServices.categoryName')}
            value={addCategoryInput}
            onChange={(e) => setAddCategoryInput(e.target.value)}
            placeholder={t('adminServices.category')}
            fullWidth
            data-testid="input-new-category-name"
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setAddCategoryOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={confirmAddCategory}
            disabled={!addCategoryInput.trim()}
          >
            {t('adminServices.addService')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={serviceDialogPaperProps}
        TransitionProps={{ onExited: handleServiceDialogExited }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          {editingId == null
            ? t('adminServices.newService')
            : isPlaceholderServiceId(editingId) && !formData.selectedEposNowProductId
            ? t('adminServices.completeService')
            : t('adminServices.editService')}
          <IconButton
            aria-label={t('common.close')}
            onClick={closeDialog}
            edge="end"
            size="small"
            data-testid="button-close-service-dialog"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          {isNewOfferingDialog ? (
            <TextField
              select
              required
              label={t('adminServices.eposProductSelect')}
              value={formData.selectedEposNowProductId}
              onChange={(e) => {
                setFormData({ ...formData, selectedEposNowProductId: e.target.value });
                if (fieldErrors.eposNowProductId)
                  setFieldErrors((prev) => ({ ...prev, eposNowProductId: undefined }));
              }}
              error={Boolean(fieldErrors.eposNowProductId)}
              helperText={fieldErrors.eposNowProductId}
              fullWidth
              data-testid="select-epos-product"
              InputLabelProps={{ sx: outlinedLabelSx }}
            >
              <MenuItem value="" disabled>
                {t('adminServices.eposProductPlaceholder')}
              </MenuItem>
              {formData.selectedEposNowProductId && !eposSelectShowsSelected && (
                <MenuItem value={formData.selectedEposNowProductId}>
                  {formData.name?.trim() || formData.selectedEposNowProductId}
                </MenuItem>
              )}
              {eposProductOptions.map((row) => (
                <MenuItem
                  key={`${row.id}-${row.eposNowProductId}`}
                  value={String(row.eposNowProductId)}
                >
                  {row.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Box sx={{ mb: 1 }} data-testid="text-service-name-display">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {t('adminServices.name')}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {formData.name || '—'}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <TextField
              label={t('adminServices.durationMinLabel')}
              type="number"
              value={formData.durationMins}
              onChange={(e) => {
                setFormData({ ...formData, durationMins: parseInt(e.target.value, 10) || 0 });
                if (fieldErrors.durationMins)
                  setFieldErrors((prev) => ({ ...prev, durationMins: undefined }));
              }}
              data-testid="input-service-duration"
              error={Boolean(fieldErrors.durationMins)}
              helperText={fieldErrors.durationMins || t('adminServices.durationOptionalHelper')}
              FormHelperTextProps={{ sx: { mt: 0.5 } }}
              InputLabelProps={{ sx: outlinedLabelSx }}
            />
            <TextField
              label={t('adminServices.bufferBefore')}
              type="number"
              value={formData.bufferBeforeMins}
              onChange={(e) => {
                setFormData({ ...formData, bufferBeforeMins: parseInt(e.target.value, 10) || 0 });
                if (fieldErrors.bufferBeforeMins)
                  setFieldErrors((prev) => ({ ...prev, bufferBeforeMins: undefined }));
              }}
              error={Boolean(fieldErrors.bufferBeforeMins)}
              helperText={fieldErrors.bufferBeforeMins}
              InputLabelProps={{ sx: outlinedLabelSx }}
            />
            <TextField
              label={t('adminServices.bufferAfter')}
              type="number"
              value={formData.bufferAfterMins}
              onChange={(e) => {
                setFormData({ ...formData, bufferAfterMins: parseInt(e.target.value, 10) || 0 });
                if (fieldErrors.bufferAfterMins)
                  setFieldErrors((prev) => ({ ...prev, bufferAfterMins: undefined }));
              }}
              error={Boolean(fieldErrors.bufferAfterMins)}
              helperText={fieldErrors.bufferAfterMins}
              InputLabelProps={{ sx: outlinedLabelSx }}
            />
          </Box>
          {staffList && staffList.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('adminServices.assignedStaff')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {staffList
                  .filter((s) => s.active)
                  .map((s) => {
                    const sid = String(s.id);
                    const selected = formData.staffIds.includes(sid);
                    return (
                      <Chip
                        key={sid}
                        label={s.displayName}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleStaff(sid)}
                        data-testid={`badge-staff-${sid}`}
                      />
                    );
                  })}
              </Box>
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(_, checked) => setFormData({ ...formData, active: checked })}
                data-testid="switch-service-active"
              />
            }
            label={t('adminServices.active')}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={closeDialog}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            data-testid="button-save-service"
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('common.saving')
              : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
